/**
 * ConfirmLiabilityMatchDialog — Phase 85.1
 *
 * "Is this payment for your Suzuki Brezza loan?" — the confirmation step that
 * stands between a detected debt payment and a real link. Deliberately honest:
 * it never claims the lender-reported balance changed.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { formatMoney } from '@/lib/format';

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
  } catch { return iso; }
}

export function ConfirmLiabilityMatchDialog({
  open, onOpenChange, item, liabilities = [], onConfirm, onReject, busy = false,
}) {
  const [picking, setPicking] = useState(false);
  const [chosen, setChosen] = useState('');

  if (!item) return null;
  const amount = formatMoney(item.total_payment ?? item.amount ?? 0, item.currency || 'INR');
  const suggestedName = item.liability_name;

  const close = () => { setPicking(false); setChosen(''); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-lg" data-testid="confirm-liability-match-dialog">
        <DialogHeader>
          <DialogTitle>Confirm this loan payment</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-muted/30 px-4 py-3">
            <p className="text-sm font-medium" data-testid="confirm-match-headline">
              {amount} · {item.description || 'Payment'} · {fmtDate(item.payment_date || item.txn_date)}
            </p>
          </div>

          {!picking && suggestedName && (
            <p className="text-sm" data-testid="confirm-match-question">
              Is this payment for your <span className="font-semibold">{suggestedName}</span>?
            </p>
          )}

          {(picking || !suggestedName) && (
            <div className="space-y-2">
              <p className="text-sm">Which loan is this payment for?</p>
              <Select value={chosen} onValueChange={setChosen}>
                <SelectTrigger data-testid="confirm-match-liability-select">
                  <SelectValue placeholder="Choose a liability" />
                </SelectTrigger>
                <SelectContent>
                  {liabilities.map((l) => (
                    <SelectItem key={l.id} value={l.id} data-testid={`confirm-match-option-${l.id}`}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <p className="text-xs text-muted-foreground leading-relaxed" data-testid="confirm-match-note">
            Linking this payment records it against the loan's activity and payoff
            estimates, and counts it toward any goal that includes this loan. It does
            not change the balance your lender reported — you can update that yourself
            or import a lender statement.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="ghost" size="sm" disabled={busy}
            onClick={() => onReject?.(item)}
            data-testid="confirm-match-no"
          >
            No
          </Button>
          {!picking && suggestedName && (
            <Button
              variant="secondary" size="sm" disabled={busy}
              onClick={() => setPicking(true)}
              data-testid="confirm-match-choose-another"
            >
              Choose another liability
            </Button>
          )}
          <Button
            size="sm"
            disabled={busy || ((picking || !suggestedName) && !chosen)}
            onClick={() => onConfirm?.(item, picking || !suggestedName ? chosen : null)}
            data-testid="confirm-match-yes"
          >
            {picking || !suggestedName ? 'Link to this loan' : 'Yes, link it'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ConfirmLiabilityMatchDialog;
